import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { FEATURE_ROWS, PLAN_DATA, PLAN_ORDER, RECOMMENDED_PLAN, includedCount, planValueLine, rowState } from "@/lib/plans";
import { useTheme } from "next-themes";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Page, PageHeader, Stats, Section, List, Row, Empty, TextLink, Loading, humanize } from "@/components/calm/Calm";
import { fmtDay } from "@/lib/dates";
import { toast } from "sonner";
import InfoTip from "@/components/ui/info-tip";

function formatSeatPrice(tier, interval = "year", monthlyCents = {}) {
  if (interval === "month") {
    const cents = Number(monthlyCents[tier.id]) || 0;
    if (cents <= 0) return "Contact us";
    return `$${(cents / 100).toLocaleString()}`;
  }
  // software_tiers.price_cents is the annual per-seat amount. Display the
  // monthly equivalent only; checkout still charges the yearly Stripe price.
  const yearlyCents = Number(tier.price_cents) || 0;
  if (yearlyCents <= 0) return "Contact us";
  const monthlyDollars = Math.round(yearlyCents / 12) / 100;
  const shown = Number.isInteger(monthlyDollars)
    ? monthlyDollars.toLocaleString()
    : monthlyDollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${shown}`;
}

export default function BillingPage() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [brokerageTiers, setBrokerageTiers] = useState([]);
  const [seatCounts, setSeatCounts] = useState({});
  const [officeInterval, setOfficeInterval] = useState("year");
  const [brokerageCheckoutLoading, setBrokerageCheckoutLoading] = useState(null);
  const [addons, setAddons] = useState([]);
  const [addonToggleLoading, setAddonToggleLoading] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  const loadBilling = useCallback(() => {
    api.get("/billing/overview").then(r => { setBilling(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadBilling(); }, [loadBilling]);

  // v3: brokerage per-seat tiers (Boutique/Growth/Enterprise/Franchise) -- separate
  // subscription mechanism from the agent paygo packages above, see /billing/subscribe-brokerage.
  useEffect(() => {
    api.get("/software-tiers", { params: { tier_kind: "brokerage" } })
      .then(r => setBrokerageTiers(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const loadAddons = useCallback(() => {
    api.get("/addons").then(r => setAddons(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  useEffect(() => { loadAddons(); }, [loadAddons]);

  const handleAddonToggle = async (key, nextEnabled) => {
    setAddonToggleLoading(key);
    try {
      if (nextEnabled) {
        const { data } = await api.post(`/addons/${key}/checkout`, {
          origin_url: window.location.origin,
        });
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        toast.error("Could not start add-on checkout");
      } else {
        const { data } = await api.post(`/addons/${key}/disable`, {
          return_url: window.location.href,
        });
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        toast.error("Could not open billing portal");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update add-on");
    } finally {
      setAddonToggleLoading(null);
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { toast.info("Payment status check timed out."); setSearchParams({}); return; }
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Payment received"); setSearchParams({}); loadBilling(); return; }
        if (data.status === "expired") { toast.error("Payment session expired."); setSearchParams({}); return; }
        attempts++; setTimeout(poll, 2000);
      } catch { toast.error("Error checking payment status."); setSearchParams({}); }
    };
    poll();
  }, [searchParams, setSearchParams, loadBilling]);

  useEffect(() => {
    const sessionId = searchParams.get("brokerage_session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { toast.info("Payment status check timed out."); setSearchParams({}); return; }
      try {
        const { data } = await api.get(`/billing/subscribe-brokerage/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Office plan active"); setSearchParams({}); loadBilling(); return; }
        attempts++; setTimeout(poll, 2000);
      } catch { toast.error("Error checking payment status."); setSearchParams({}); }
    };
    poll();
  }, [searchParams, setSearchParams, loadBilling]);

  useEffect(() => {
    const sessionId = searchParams.get("addon_session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 5) { toast.info("Payment status check timed out."); setSearchParams({}); return; }
      try {
        const { data } = await api.get(`/addons/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Add-on active");
          setSearchParams({});
          loadAddons();
          loadBilling();
          return;
        }
        if (data.status === "expired") { toast.error("Payment session expired."); setSearchParams({}); return; }
        attempts++; setTimeout(poll, 2000);
      } catch { toast.error("Error checking add-on payment status."); setSearchParams({}); }
    };
    poll();
  }, [searchParams, setSearchParams, loadAddons, loadBilling]);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data.url) window.location.href = data.url;
      else toast.error("Could not open billing portal");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not open billing portal"); } finally { setPortalLoading(false); }
  };

  const handleUpgrade = async () => {
    if (portalLoading || checkoutLoading) return;
    const onFree = !(billing?.monthly_price > 0);
    if (onFree) {
      // Clay Free: no Stripe subscription to manage — send to checkout for first paid plan
      const paid = (billing?.available_plans || []).find(p => !p.active && (p.amount || 0) > 0);
      if (!paid) {
        toast.info("Pick a paid plan below first.");
        return;
      }
      setCheckoutLoading(true);
      try {
        const { data } = await api.post("/billing/checkout", {
          package_id: paid.key || paid.id,
          origin_url: window.location.origin,
        });
        if (data.url) window.location.href = data.url;
        else toast.error("Checkout failed");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Checkout failed");
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }
    await handlePortal();
  };

  const handleCheckout = async (packageId) => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { package_id: packageId, origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); } finally { setCheckoutLoading(false); }
  };

  const handleBrokerageCheckout = async (tierId, seatMin) => {
    const seatCount = seatCounts[tierId] || seatMin;
    setBrokerageCheckoutLoading(tierId);
    try {
      const { data } = await api.post("/billing/subscribe-brokerage", {
        tier_id: tierId, seat_count: seatCount, origin_url: window.location.origin, billing_interval: officeInterval,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); } finally { setBrokerageCheckoutLoading(null); }
  };

  if (loading) return <Loading />;
  if (!billing) return null;

  const sub = {
    plan:        billing.plan_name || billing.plan || "Clay",
    price:       `$${(billing.monthly_price || 0).toLocaleString()} / mo`,
    used:        billing.total_used || 0,
    total:       billing.total_allocation ?? 0,
    overage:     billing.overage || 0,
    overageCost: billing.overage_cost || 0,
    projectedTotal: billing.projected_total || billing.monthly_price || 0,
    cycleDay:    billing.cycle_day || new Date().getDate(),
  };

  // FIXED 2026-08-16: this cycle/next-charge label was hardcoded to
  // "April"/"Apr 30" -- shown to every user regardless of the actual date,
  // every day of the year. sub.cycleDay was already computed from real
  // billing data (billing.cycle_day) but never actually used anywhere.
  const _now = new Date();
  const _cycleLabel = `${_now.toLocaleDateString('en-US', { month: 'long' })} cycle, closes ${new Date(_now.getFullYear(), _now.getMonth() + 1, 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  const _nextChargeDate = (() => {
    let d = new Date(_now.getFullYear(), _now.getMonth(), sub.cycleDay);
    if (d <= _now) d = new Date(_now.getFullYear(), _now.getMonth() + 1, sub.cycleDay);
    return d;
  })();
  const isOffice = !!billing.is_office;
  const officeLive = isOffice && !!billing.office_plan_live;
  const canManage = billing.can_manage !== false;
  const _officeEnd = billing.plan_period_end ? new Date(billing.plan_period_end) : null;
  const _officeEndLabel = _officeEnd && !isNaN(_officeEnd) ? _officeEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const _nextChargeLabel = isOffice
    ? (!officeLive ? 'No office plan yet'
      : billing.cancel_at_period_end ? (_officeEndLabel ? `Plan ends ${_officeEndLabel}` : 'Plan ends at period end')
      : (_officeEndLabel ? `Next charge ${_officeEndLabel}` : 'Next charge at renewal'))
    : `Next charge ${_nextChargeDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  const plans = (billing.available_plans && billing.available_plans.length > 0)
    ? billing.available_plans.map(p => ({
        id:        p.key,
        name:      p.name,
        price:     p.amount,
        creditGrant: p.credit_grant_dollars ?? (p.credits_per_month ?? 0),
        description: p.description || '',
        current:   p.active,
      }))
    : [];

  const busy = portalLoading || checkoutLoading;
  const mono = { fontFamily: F.mono, fontSize: 12.5 };
  const planName = isOffice ? (billing.plan_name || 'No office plan')
    : sub.plan ? String(sub.plan).charAt(0).toUpperCase() + String(sub.plan).slice(1) : 'Clay';
  const officeEvery = billing.billing_interval === 'month' ? 'a month' : 'a year';
  const invoices = billing.invoices || [];

  return (
    <Page testid="billing-page">
      <PageHeader title="Billing" help="plans-billing" subtitle={_cycleLabel}
        action={isOffice
          ? (officeLive && canManage ? <Btn dark={dark} data-testid="billing-main-manage" disabled={busy} onClick={handlePortal}>{portalLoading ? 'Opening…' : 'Manage billing'}</Btn> : null)
          : billing?.monthly_price > 0
          ? <Btn dark={dark} data-testid="billing-main-manage" disabled={busy} onClick={handlePortal}>{portalLoading ? 'Opening…' : 'Manage billing'}</Btn>
          : <Btn dark={dark} data-testid="billing-main-upgrade" disabled={busy} onClick={handleUpgrade}>{busy ? 'Opening…' : 'Upgrade plan'}</Btn>} />

      <Stats testid="billing-stats" items={isOffice ? [
        { value: `$${(billing.projected_total || 0).toLocaleString()}`, label: _nextChargeLabel },
        officeLive ? { value: String(billing.seat_count || 0), label: billing.seat_count === 1 ? 'Seat' : 'Seats' } : null,
      ].filter(Boolean) : [
        { value: `$${(sub.projectedTotal).toLocaleString()}`, label: _nextChargeLabel },
        sub.total > 0 ? { value: `$${sub.used.toLocaleString()}`, label: `of $${sub.total.toLocaleString()} credit used` } : { value: sub.price.replace(' / mo', ''), label: `${planName} plan, a month` },
        sub.overageCost > 0 ? { value: `+$${sub.overageCost.toLocaleString()}`, label: 'Overage this cycle', attention: true } : null,
      ].filter(Boolean)} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]" style={{ gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 36, minWidth: 0 }}>

          <Section title="Plan" testid="billing-plan">
            <List>
              {isOffice ? (
                <Row title={planName} testid="billing-office-plan"
                  meta={officeLive
                    ? [billing.plan_description, { text: `$${(billing.seat_price || 0).toLocaleString()} per seat ${officeEvery}`, figure: true }]
                    : [canManage ? 'Pick an office plan below to give your agents seats.' : 'Your office manages this plan.']}
                  next={officeLive && _officeEndLabel ? [billing.cancel_at_period_end ? `Ends ${_officeEndLabel}` : `Renews ${_officeEndLabel}`] : undefined}
                  status={officeLive && billing.cancel_at_period_end ? 'Cancelling' : null} />
              ) : (
              <Row title={planName}
                meta={[{ text: sub.price, figure: true }, '24h turnaround included']}
                next={sub.total > 0 ? [`$${(sub.total - sub.used).toLocaleString()} credit left this cycle`] : ['No credit. Shoots billed at standard rates.']}
                aside={<TextLink hit testid="billing-upgrade" onClick={!busy ? handleUpgrade : undefined}>{busy ? 'Opening…' : 'Upgrade'}</TextLink>} />
              )}
            </List>
          </Section>

          {isOffice && canManage && !officeLive && brokerageTiers.length > 0 && (
            <Section title="Office plans" note={`Per seat, billed ${officeInterval === 'month' ? 'monthly, no lock-in' : 'yearly'}. Media billed separately.`} testid="billing-office-plans"
              action={<div role="group" aria-label="Billing period" style={{ display: 'inline-flex', gap: 12 }}>
                {[['year', 'Yearly'], ['month', 'Monthly']].map(([k, label]) => (
                  <TextLink key={k} hit testid={`billing-office-${k}`} onClick={() => setOfficeInterval(k)}>
                    <span style={{ color: officeInterval === k ? p.ink : p.ink3, fontWeight: officeInterval === k ? 500 : 400 }} aria-pressed={officeInterval === k}>{label}</span>
                  </TextLink>))}
              </div>}>
              <List>
                {brokerageTiers.map(t => {
                  const seatMin = t.seat_band_min ?? 1;
                  const seatMax = t.seat_band_max;
                  const seatCount = seatCounts[t.id] ?? seatMin;
                  return (
                    <Row key={t.id} title={t.name}
                      meta={[{ text: `${formatSeatPrice(t, officeInterval, billing.office_monthly_seat_cents || {})} per seat a month`, figure: false }, seatMax ? `${seatMin} to ${seatMax} seats` : `${seatMin}+ seats`]}
                      aside={<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input aria-label={`Seats for ${t.name}`} type="number" min={seatMin} max={seatMax || undefined} value={seatCount}
                          onChange={e => setSeatCounts(s => ({ ...s, [t.id]: Math.max(seatMin, parseInt(e.target.value, 10) || seatMin) }))}
                          style={{ width: 64, minHeight: 44, boxSizing: 'border-box', padding: '6px 8px', border: `1px solid ${p.hairS}`, borderRadius: 2, fontFamily: F.mono, fontSize: 13, background: p.bg, color: p.ink }} />
                        <Btn dark={dark} disabled={brokerageCheckoutLoading === t.id} onClick={() => handleBrokerageCheckout(t.id, seatMin)}>{brokerageCheckoutLoading === t.id ? 'Redirecting…' : 'Subscribe'}</Btn>
                      </div>} />
                  );
                })}
              </List>
            </Section>
          )}

          {addons.length > 0 && (
            <Section title="Add-ons" testid="billing-addons"
              note={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Add one feature without changing plans. Included in Diamond.<InfoTip label="About add-ons">Billed monthly. Disable opens the billing portal to cancel that add-on.</InfoTip></span>}>
              <List>
                {addons.map(a => (
                  <Row key={a.key} title={a.name}
                    meta={[a.description, a.included_by_current_plan ? null : { text: `$${a.price_cad_monthly.toFixed(2)}/mo`, figure: true }].filter(Boolean)}
                    status={a.included_by_current_plan ? `Included in ${a.included_in_plan}` : a.standalone_enabled ? 'On' : null}
                    aside={a.included_by_current_plan ? null : a.standalone_enabled
                      ? <TextLink onClick={() => addonToggleLoading !== a.key && handleAddonToggle(a.key, false)}>{addonToggleLoading === a.key ? 'Opening…' : 'Manage'}</TextLink>
                      : <Btn variant="primary" dark={dark} disabled={addonToggleLoading === a.key} onClick={() => handleAddonToggle(a.key, true)}>{addonToggleLoading === a.key ? 'Checkout…' : 'Enable'}</Btn>} />
                ))}
              </List>
            </Section>
          )}

          <Section title="Invoices" testid="billing-invoices">
            {invoices.length === 0 ? <Empty>No invoices yet. They appear here after your first charge.</Empty> : (
              <List>
                {invoices.map(inv => (
                  <Row key={inv.invoice_id}
                    title={inv.description || `${planName} cycle`}
                    meta={[{ text: inv.invoice_id, figure: true }, inv.created_at ? fmtDay(inv.created_at) : inv.period].filter(Boolean)}
                    status={humanize(inv.status)} attention={['open', 'uncollectible', 'failed', 'past_due'].includes(inv.status)}
                    aside={<span style={{ ...mono, color: p.ink }}>${(inv.amount || 0).toLocaleString()}</span>} />
                ))}
              </List>
            )}
          </Section>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 36, minWidth: 0 }}>
          <Section title="Payment method" testid="billing-payment">
            <div style={{ borderTop: `1px solid ${p.hair}`, paddingTop: 14, fontSize: 13, color: p.ink3, lineHeight: 1.5 }}>
              Cards and invoices are managed securely in Stripe.
              <div style={{ marginTop: 10 }}>
                <TextLink hit testid="billing-portal" onClick={!portalLoading ? handlePortal : undefined}>{portalLoading ? 'Opening…' : 'Manage payment methods'}</TextLink>
              </div>
            </div>
          </Section>

          {!isOffice && (
          <Section title="Plans" testid="billing-plans">
            <List>
              {plans.map(plan => (
                <Row key={plan.id} wide={false}
                  title={<>{plan.name}{plan.id === RECOMMENDED_PLAN && !plan.current && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: p.ink3 }}>Recommended</span>}</>}
                  meta={PLAN_DATA[plan.id] ? [`${includedCount(plan.id)} of ${FEATURE_ROWS.length} features`, planValueLine(PLAN_DATA[plan.id], false)] : [plan.description || 'Media booked separately.']}
                  status={plan.current ? 'Current' : null}
                  aside={<span style={{ ...mono, color: p.ink }}>${plan.price.toLocaleString()}</span>} />
              ))}
            </List>
            <div style={{ marginTop: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: p.ink2, marginBottom: 8 }}>Compare plans</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums', tableLayout: 'fixed' }}>
                <thead><tr>
                  <th style={{ textAlign: 'left', fontWeight: 500, padding: '6px 6px 6px 0', color: p.ink3, width: '34%' }}>Feature</th>
                  {PLAN_ORDER.map(id => <th key={id} style={{ textAlign: 'center', fontWeight: 500, padding: '6px 2px', color: p.ink, overflowWrap: 'anywhere' }}>{PLAN_DATA[id].name}</th>)}
                </tr></thead>
                <tbody>{FEATURE_ROWS.map(r => (
                  <tr key={r.label} style={{ borderTop: `1px solid ${p.hair}` }}>
                    <td style={{ padding: '8px 6px 8px 0', color: p.ink2 }}>{r.label}</td>
                    {PLAN_ORDER.map(id => { const st = rowState(r, id); return (
                      <td key={id} style={{ textAlign: 'center', padding: '6px 2px', color: st === 'new' || st === 'inherited' ? p.ink : p.ink4, fontFamily: st === 'addon' ? F.mono : undefined, fontSize: st === 'addon' ? 10.5 : 12 }}>
                        {st === 'new' || st === 'inherited' ? '✓' : st === 'addon' ? `+$${r.addon}` : '·'}
                      </td>); })}
                  </tr>))}</tbody>
              </table>
            </div>
          </Section>
          )}
        </div>
      </div>
    </Page>
  );
}
